import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Play, Sparkles, Infinity as Inf, ShieldCheck, Smartphone, Upload, CheckCircle2, Wallet, Clapperboard } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/providers";
import { useSeo } from "@/lib/seo";
import { CourseCard } from "@/components/site/CourseCard";
import { FaqList, FAQS } from "@/components/site/Faq";
import { StudentReels } from "@/components/site/StudentReels";
import { Star, Spiral, Arrow, BadgeCircle, Floater } from "@/components/site/Doodles";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.55 } } };
const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.12 } } };

const HeroScreen = () => (
  <div className="relative">
    <Floater className="absolute -top-8 -left-4 z-20"><Star className="w-14 h-14" color="#fff" /></Floater>
    <Floater className="absolute -bottom-10 right-6 z-20" delay={1}><Star className="w-10 h-10" /></Floater>
    <div className="relative rounded-[2rem] bg-[var(--kh-deep)] sticker p-4 rotate-2">
      <div className="flex gap-1.5 mb-3">{["#FFFFFF", "#E4D2F7", "#B98AE8"].map((c) => <span key={c} className="w-3 h-3 rounded-full border border-[var(--kh-deep)]" style={{ background: c }} />)}</div>
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-[#4A1580]">
        <motion.div className="absolute top-0 left-0 w-16 h-16 rounded-full bg-[#FFFFFF] border-2 border-[var(--kh-deep)]"
          animate={{ x: [20, 220, 120, 20], y: [30, 60, 150, 30], scale: [1, 0.8, 1.2, 1] }} transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-0 left-0 w-14 h-14 bg-[#E4D2F7] border-2 border-[var(--kh-deep)]"
          animate={{ x: [260, 80, 300, 260], y: [140, 40, 90, 140], rotate: [0, 180, 360, 360] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} />
        <motion.div className="absolute top-0 left-0 w-0 h-0 border-l-[28px] border-r-[28px] border-b-[48px] border-l-transparent border-r-transparent border-b-[#B98AE8]"
          animate={{ x: [160, 300, 40, 160], y: [100, 20, 120, 100], rotate: [0, -120, 90, 0] }} transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }} />
        <div className="absolute bottom-3 inset-x-3 flex items-center justify-center">
          <span className="font-display text-3xl sm:text-4xl text-white drop-shadow-[3px_3px_0_#5A1C96]">حرّك خيالك</span>
        </div>
      </div>
      <div className="mt-4 space-y-2" aria-hidden>
        {[["#FFFFFF", "70%", "0%"], ["#E4D2F7", "45%", "20%"], ["#C9A3EE", "55%", "35%"]].map(([c, w, m], i) => (
          <div key={i} className="h-3 rounded-full bg-white/15 relative overflow-hidden">
            <motion.div className="absolute h-full rounded-full" style={{ background: c, width: w, right: m }}
              animate={{ x: [0, -18, 0] }} transition={{ duration: 3 + i, repeat: Infinity }} />
          </div>
        ))}
        <motion.div className="absolute bottom-[4.4rem] w-0.5 h-16 bg-[#FFFFFF]" animate={{ right: ["8%", "88%", "8%"] }} transition={{ duration: 6, repeat: Infinity, ease: "linear" }} />
      </div>
    </div>
    <BadgeCircle className="absolute -bottom-14 -left-10 w-36 h-36 hidden sm:block" />
    <div className="absolute -bottom-6 -left-0 sm:left-[1.9rem] sm:-bottom-[1.6rem] place-items-center w-14 h-14 rounded-full bg-[#FFFFFF] border-2 border-[var(--kh-deep)] hidden sm:grid"><Play fill="#7F2BC6" className="text-[var(--kh-violet)]" size={22} /></div>
  </div>
);

const HeroAccountBtn = () => {
  const { user } = useAuth();
  const cls = "btn-pop btn-secondary inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold text-lg";
  return user ? (
    <Link to="/dashboard" data-testid="hero-dashboard-btn" className={cls}>لوحة التحكم</Link>
  ) : (
    <Link to="/register" data-testid="hero-register-btn" className={cls}>اعمل حسابك مجاناً</Link>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden grain bg-brand">
    <div className="absolute inset-0 grid-paper opacity-80" />
    <div className="absolute -top-40 -right-40 w-[36rem] h-[36rem] rounded-full bg-white/10 blur-[120px]" />
    <div className="absolute bottom-0 left-0 w-[24rem] h-[24rem] rounded-full bg-[var(--kh-violet-2)]/15 blur-[120px]" />
    <Spiral className="absolute top-24 left-[42%] w-24 h-24 opacity-60 hidden lg:block" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-28 lg:pt-24 grid lg:grid-cols-12 gap-14 items-center">
      <motion.div variants={stagger} initial="hidden" animate="show" className="lg:col-span-7 space-y-7">
        <motion.span variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-purple-50 border border-line text-sm text-brand">
          <Sparkles size={15} /> أكاديمية موشن جرافيك وأنيميشن بالعربي
        </motion.span>
        <motion.h1 variants={fadeUp} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.15] text-ink" data-testid="hero-title">
          خرابيشك الأولى
          <span className="inline-block mx-3 text-brand">هتتحرك</span>
          <br />
          <span>وتبقى أنيميشن.</span>
        </motion.h1>
        <motion.p variants={fadeUp} className="text-base sm:text-lg text-soft max-w-xl leading-relaxed">
          كورسات مسجلة في الموشن جرافيك والأنيميشن، من أول فكرة على الورق لحد ما تطلع شغل يتحط في البورتفوليو. اتعلم بسرعتك، وارجع للدرس وقت ما تحب.
        </motion.p>
        <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-4 relative">
          <Link to="/courses" data-testid="hero-browse-courses-btn" className="btn-pop btn-primary inline-flex items-center gap-2 px-7 py-4 rounded-full font-bold text-lg">
            اكتشف الكورسات <ArrowLeft size={20} />
          </Link>
          <HeroAccountBtn />
          <Arrow className="absolute -top-20 left-4 w-24 h-20 hidden md:block rotate-12" />
        </motion.div>
        <motion.div variants={fadeUp} className="flex flex-wrap gap-6 pt-4 text-sm text-soft">
          <span className="flex items-center gap-2"><Inf size={18} className="text-brand" /> وصول دائم للكورس</span>
          <span className="flex items-center gap-2"><Smartphone size={18} className="text-brand" /> من الموبايل أو اللابتوب</span>
          <span className="flex items-center gap-2"><ShieldCheck size={18} className="text-brand" /> دفع محلي آمن</span>
        </motion.div>
      </motion.div>
      <motion.div initial={{ opacity: 0, scale: 0.9, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.8, delay: 0.3 }} className="lg:col-span-5">
        <HeroScreen />
      </motion.div>
    </div>
  </section>
);

const WORDS = ["موشن جرافيك", "أنيميشن 2D", "كاركتر ديزاين", "After Effects", "3D Motion", "ستوري بورد", "تايبوجرافي متحركة"];
const Marquee = () => (
  <div className="relative bg-[#FFFFFF] border-y-2 border-[var(--kh-deep)] -rotate-1 overflow-hidden py-4" aria-hidden>
    <div className="flex w-max kh-marquee">
      {[...WORDS, ...WORDS, ...WORDS, ...WORDS].map((w, i) => (
        <span key={i} className="flex items-center gap-6 px-6 font-display text-2xl sm:text-3xl text-[var(--kh-violet)] whitespace-nowrap">{w}<Star className="w-6 h-6" color="#7F2BC6" /></span>
      ))}
    </div>
  </div>
);

const Highlights = () => {
  const [courses, setCourses] = useState(null);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => setCourses([])); }, []);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-24" data-testid="home-courses-section">
      <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
        <div>
          <span className="text-xs tracking-[0.3em] text-brand font-semibold">COURSES</span>
          <h2 className="font-display text-4xl sm:text-5xl text-ink mt-2">الكورسات اللي <span className="text-brand">هتحركك</span></h2>
        </div>
        <Link to="/courses" data-testid="home-all-courses-link" className="inline-flex items-center gap-2 text-soft hover:text-brand">كل الكورسات <ArrowLeft size={18} /></Link>
      </div>
      {courses && courses.length === 0 && (
        <div className="relative rounded-3xl border-2 border-dashed border-[#C9A3EE] bg-soft p-12 text-center" data-testid="home-courses-empty">
          <Clapperboard className="mx-auto mb-4 text-brand" size={42} />
          <p className="font-heading text-2xl text-ink mb-2">الكورسات جاية قريب جداً</p>
          <p className="text-soft">بنجهزلك محتوى يستاهل. اعمل حسابك عشان تكون أول واحد يعرف.</p>
        </div>
      )}
      <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
        {(courses || []).slice(0, 6).map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}
      </div>
    </section>
  );
};

const FEATURES = [
  [Clapperboard, "مشاريع حقيقية", "كل كورس مبني على مشاريع عملية تطلع منها بشغل للبورتفوليو.", "#9B58E5"],
  [Inf, "اتعلم على مزاجك", "الدروس مسجلة ومتاحة ليك، توقف وتكمل من نفس النقطة.", "#6521AC"],
  [CheckCircle2, "تابع تقدمك", "لوحة شخصية بتوضحلك خلصت إيه وفاضل إيه.", "#9B58E5"],
  [ShieldCheck, "محتوى محمي", "حسابك شخصي والمحتوى محمي عشان قيمة اللي بتتعلمه تفضل محفوظة.", "#6521AC"],
];

const Features = () => (
  <section className="relative py-24 bg-soft overflow-hidden" data-testid="home-why-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="max-w-3xl mb-14 space-y-5">
        <h2 className="font-display text-4xl sm:text-5xl text-ink" data-testid="why-title">ليه <span className="text-brand">خرابيش أكاديمي</span>؟</h2>
        <p className="text-base sm:text-lg text-soft leading-relaxed" data-testid="why-description">
          لأنك هتتعلم على إيد مدربين بخبرة أكتر من 8 سنين في المجال! ومش هتكون لوحدك في رحلة التعلم؛ هنوفرلك متابعة ودعم مباشر يساعدوك تطوّر مهاراتك وتطبّق اللي اتعلمته.
        </p>
      </div>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {FEATURES.map(([Icon, t, d, c], i) => (
          <motion.div key={t} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className={i % 2 ? "lg:mt-10" : ""}>
            <div className="kh-lift h-full rounded-3xl p-7 bg-white border-2 border-line" data-testid={`why-card-${i}`}>
              <span className="grid place-items-center w-14 h-14 rounded-2xl mb-5" style={{ background: c }}><Icon className="text-[var(--kh-violet)]" /></span>
              <h3 className="font-heading text-xl font-bold mb-2 text-ink">{t}</h3>
              <p className="text-sm text-soft leading-relaxed">{d}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const STEPS = [[Wallet, "اختار الكورس وحوّل", "فودافون كاش أو إنستاباي"], [Upload, "ارفع الإيصال", "من صفحة الدفع في ثواني"], [CheckCircle2, "المراجعة والتفعيل", "بنراجع ونفعّل الكورس لحسابك"], [Play, "ابدأ اتعلم", "وكمّل من مكان ما وقفت"]];
const Steps = () => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
    <h2 className="font-display text-4xl text-ink mb-12 text-center">الاشتراك في <span className="text-brand">4 خطوات</span></h2>
    <div className="relative grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
      <div className="hidden lg:block absolute top-10 right-[12%] left-[12%] border-t-4 border-dotted border-[#D9C4F2]" />
      {STEPS.map(([Icon, t, d], i) => (
        <div key={t} className="relative text-center space-y-3">
          <span className="relative mx-auto grid place-items-center w-20 h-20 rounded-full bg-white border-2 border-[var(--kh-violet)] z-10">
            <Icon className="text-brand" size={30} />
            <span className="absolute -top-2 -right-2 grid place-items-center w-8 h-8 rounded-full bg-[#FFFFFF] text-[var(--kh-violet)] font-display border-2 border-[var(--kh-deep)]">{i + 1}</span>
          </span>
          <h3 className="font-heading text-lg font-bold text-ink">{t}</h3>
          <p className="text-sm text-soft">{d}</p>
        </div>
      ))}
    </div>
  </section>
);

const HomeFaq = () => (
  <section className="max-w-4xl mx-auto px-4 sm:px-6 py-20">
    <h2 className="font-display text-4xl sm:text-5xl text-ink mb-10">أسئلة <span className="text-brand">بتتسأل كتير</span></h2>
    <FaqList items={FAQS.slice(0, 5)} />
  </section>
);

const FinalCta = () => {
  const { user } = useAuth();
  return (
  <section className="max-w-7xl mx-auto px-4 sm:px-6">
    <div className="relative overflow-hidden rounded-[2.5rem] bg-[var(--kh-deep)] sticker p-10 sm:p-16 grain">
      <Spiral className="absolute -left-6 -top-6 w-40 h-40 opacity-50" color="#fff" />
      <Floater className="absolute bottom-6 left-1/3"><Star className="w-12 h-12" color="#fff" /></Floater>
      <div className="relative max-w-2xl space-y-6">
        <h2 className="font-display text-4xl sm:text-6xl text-white leading-tight">جاهز تحوّل الخرابيش <br />لشغل بيتحرك؟</h2>
        <p className="text-white/90 text-lg">اعمل حسابك دلوقتي، اختار الكورس، وابدأ رحلتك في عالم الموشن.</p>
        <div className="flex flex-wrap gap-4">
          {user ? (
            <Link to="/dashboard" data-testid="cta-dashboard-btn" className="btn-pop btn-primary px-8 py-4 rounded-full font-bold text-lg">روح لكورساتك</Link>
          ) : (
            <Link to="/register" data-testid="cta-register-btn" className="btn-pop btn-primary px-8 py-4 rounded-full font-bold text-lg">سجّل مجاناً</Link>
          )}
          <Link to="/courses" data-testid="cta-courses-btn" className="btn-pop btn-secondary px-8 py-4 rounded-full font-bold text-lg">شوف الكورسات</Link>
        </div>
      </div>
    </div>
  </section>
  );
};

export default function Home() {
  useSeo(null, "أكاديمية خرابيش — كورسات موشن جرافيك وأنيميشن مسجلة باللغة العربية.");
  return (
    <div data-testid="home-page" className="overflow-x-hidden">
      <Hero />
      <Marquee />
      <Highlights />
      <Features />
      <StudentReels />
      <Steps />
      <HomeFaq />
      <FinalCta />
    </div>
  );
}
